import type { Platform, Technology } from '../core/types';

interface CarouselState {
  track: HTMLElement;
  position: number;
  speed: number;
  isPaused: boolean;
  itemWidth: number;
  gap: number;
  totalWidth: number;
  isDragging: boolean;
  startX: number;
  lastX: number;
}

const carousels: CarouselState[] = [];

const initCarousel = (trackId: string, direction: 'left' | 'right', items: HTMLElement[]): CarouselState | null => {
  const track = document.getElementById(trackId);
  if (!track || items.length === 0) return null;

  const container = track.parentElement as HTMLElement;
  const gap = 24;

  track.innerHTML = '';
  if (direction === 'left') {
    items.forEach((item) => track.appendChild(item));
    items.forEach((item) => track.appendChild(item.cloneNode(true)));
  } else {
    items.forEach((item) => track.appendChild(item.cloneNode(true)));
    items.forEach((item) => track.appendChild(item));
  }

  const originalStart = direction === 'left' ? 0 : items.length;
  const originalChildren = Array.from(track.children).slice(originalStart, originalStart + items.length) as HTMLElement[];
  const totalWidth = originalChildren.reduce((sum, child) => sum + child.getBoundingClientRect().width + gap, 0);

  const state: CarouselState = {
    track,
    position: direction === 'left' ? 0 : totalWidth,
    speed: direction === 'left' ? 1.0 : -1.0,
    isPaused: false,
    itemWidth: (track.firstElementChild?.getBoundingClientRect().width ?? 180) + gap,
    gap,
    totalWidth,
    isDragging: false,
    startX: 0,
    lastX: 0,
  };

  const pause = (): void => {
    state.isPaused = true;
  };

  const resume = (): void => {
    state.isPaused = false;
  };

  const startDrag = (clientX: number): void => {
    state.isDragging = true;
    state.isPaused = true;
    state.startX = clientX;
    state.lastX = clientX;
  };

  const moveDrag = (clientX: number): void => {
    if (!state.isDragging) return;
    const delta = clientX - state.lastX;
    state.position -= delta;
    state.lastX = clientX;
  };

  const endDrag = (): void => {
    if (!state.isDragging) return;
    state.isDragging = false;
    state.isPaused = false;
  };

  container.addEventListener('mouseenter', () => {
    if (!state.isDragging) pause();
  });
  container.addEventListener('mouseleave', () => {
    if (!state.isDragging) resume();
  });

  container.addEventListener('mousedown', (event) => {
    startDrag(event.clientX);
  });

  window.addEventListener('mousemove', (event) => {
    moveDrag(event.clientX);
  });

  window.addEventListener('mouseup', endDrag);

  const onTouchStart = (event: TouchEvent): void => {
    if (event.touches.length !== 1) return;
    startDrag(event.touches[0].clientX);
  };

  const onTouchMove = (event: TouchEvent): void => {
    if (!state.isDragging || event.touches.length !== 1) return;
    event.preventDefault();
    moveDrag(event.touches[0].clientX);
  };

  const onTouchEnd = (): void => {
    endDrag();
  };

  container.addEventListener('touchstart', onTouchStart, { passive: true });
  container.addEventListener('touchmove', onTouchMove, { passive: false });
  container.addEventListener('touchend', onTouchEnd);
  container.addEventListener('touchcancel', onTouchEnd);

  carousels.push(state);
  return state;
};

const animateCarousels = (): void => {
  carousels.forEach((state) => {
    if (!state.isPaused && !state.isDragging) {
      state.position += state.speed;
    }

    if (state.speed > 0 && state.position >= state.totalWidth) {
      state.position = 0;
    } else if (state.speed < 0 && state.position <= 0) {
      state.position = state.totalWidth;
    }

    state.track.style.transform = `translateX(${-state.position}px)`;
  });

  requestAnimationFrame(animateCarousels);
};

export const createCarouselItem = (icon: string, name: string, description?: string): HTMLElement => {
  const item = document.createElement('div');
  item.className = 'carousel__item';
  item.innerHTML = `
    <img src="${icon}" alt="${name}" loading="lazy" />
    <div class="carousel__item-info">
      <div class="carousel__item-text">${name}</div>
      ${description ? `<div class="carousel__item-description">${description}</div>` : ''}
    </div>
  `;
  return item;
};

export const initCarousels = (platforms: Platform[], tech: Technology[]): void => {
  const platformItems = platforms.map((platform) => createCarouselItem(platform.icon, platform.name));
  const techItems = tech.map((item) => createCarouselItem(item.icon, item.name, item.description));

  initCarousel('platforms-track', 'left', platformItems);

  const half = Math.ceil(techItems.length / 2);
  initCarousel('tech-track-top', 'right', techItems.slice(0, half));
  initCarousel('tech-track-bottom', 'right', techItems.slice(half));

  animateCarousels();
};
